import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import * as SkeletonUtils from "three/addons/utils/SkeletonUtils.js";

/**
 * AssetRegistry — a flexible, scalable asset pipeline.
 *
 * You register assets by KEY (e.g. "dungeon/wall", "nature/tree_pine",
 * "characters/knight"). An entry can be:
 *
 *   1) A string URL, or { url }              → one model per GLTF file
 *   2) { pack, node }                        → a named sub-mesh pulled out of
 *                                              a big multi-mesh GLTF/GLB
 *
 * Packs are loaded once and shared, so a "ton of assets" split across a few
 * large files (or thousands of small ones) both work. Anything that fails to
 * load is simply marked unavailable and the game falls back to a themed
 * placeholder — so you can drop art in incrementally.
 *
 * See registerFolder() for a convention-based bulk approach.
 */
export class AssetRegistry {
  constructor() {
    this.loader = new GLTFLoader();
    this.entries = {};   // key -> entry definition
    this.models = {};    // key -> resolved THREE.Object3D
    this.available = {}; // key -> boolean
    this._packCache = {}; // url -> Promise<gltf>
  }

  /** Register many entries at once: { key: url | {url} | {pack,node} }. */
  register(manifest) {
    Object.assign(this.entries, manifest);
    return this;
  }

  /**
   * Convenience for bulk libraries following a folder convention. Given a base
   * URL and a list of file names, registers "<prefix>/<name>" -> url. Example:
   *   registerFolder("nature", "assets/nature", ["tree_pine","rock_small"])
   */
  registerFolder(prefix, baseUrl, names, ext = "gltf") {
    const m = {};
    for (const n of names) m[`${prefix}/${n}`] = `${baseUrl}/${n}.${ext}`;
    return this.register(m);
  }

  async loadAll() {
    await Promise.all(Object.keys(this.entries).map((k) => this._load(k)));
    return this;
  }

  async _load(key) {
    const entry = this.entries[key];
    try {
      let root;
      if (typeof entry === "string" || entry.url) {
        const gltf = await this.loader.loadAsync(typeof entry === "string" ? entry : entry.url);
        root = gltf.scene;
        root.animations = gltf.animations || [];
      } else if (entry.pack) {
        const gltf = await this._loadPack(entry.pack);
        const node = gltf.scene.getObjectByName(entry.node);
        if (!node) throw new Error(`node "${entry.node}" not in pack ${entry.pack}`);
        root = node.clone(true);
        root.animations = gltf.animations || [];
      } else {
        throw new Error("bad entry");
      }
      root.traverse((o) => { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; } });
      this.models[key] = root;
      this.available[key] = true;
    } catch (_) {
      this.available[key] = false; // silent → placeholder fallback
    }
  }

  _loadPack(url) {
    if (!this._packCache[url]) this._packCache[url] = this.loader.loadAsync(url);
    return this._packCache[url];
  }

  has(key) { return !!(key && this.available[key]); }
  loadedCount() { return Object.values(this.available).filter(Boolean).length; }
  totalCount() { return Object.keys(this.entries).length; }

  /** Clone a resolved model (skeleton-safe for rigged characters). */
  instance(key) {
    const src = this.models[key];
    if (!src) return null;
    const clone = SkeletonUtils.clone(src);
    clone.animations = src.animations;
    return clone;
  }

  /** Animation clips from a loaded model/library key (empty if unavailable). */
  clips(key) {
    const src = this.models[key];
    return (src && src.animations) || [];
  }

  /** Keys whose files failed to load (missing / unreadable / cloud-only). */
  missing() {
    return Object.keys(this.entries).filter((k) => !this.available[k]);
  }
}
